import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Package, DollarSign, Users, Settings, ChevronRight, Trash2, Edit, AlertCircle, Link2, CreditCard, Layers, Palette, Truck } from 'lucide-react';
import ConfigurationRulesTab from '../components/ConfigurationRulesTab';
import SalesRateCardTab from '../components/SalesRateCardTab';
import FreightRulesTab from '../components/FreightRulesTab';
import { Header } from '../components/Header';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE;

export default function PricingPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sales');
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [settings, setSettings] = useState(null);

  const fetchCatalogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/catalogs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch catalogs');
      const data = await res.json();
      setCatalogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchCatalogs();
    fetchSettings();
  }, [fetchCatalogs, fetchSettings]);

  // Auto-redirect when flows tab is selected
  useEffect(() => {
    if (activeTab === 'flows') {
      navigate('/admin/product-config');
    }
  }, [activeTab, navigate]);

  const filteredCatalogs = catalogs.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.partner_name && c.partner_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const tabs = [
    { id: 'sales', label: 'Sales Rate Cards', icon: CreditCard },
    { id: 'bom', label: 'BOM Management', icon: Layers },
    { id: 'flows', label: 'Product Config', icon: Palette },
    { id: 'catalogs', label: 'Procurement Catalogs', icon: Package },
    { id: 'freight', label: 'Freight Rules', icon: Truck },
    // Configuration Rules deprecated - pricing now driven by Sales Rate Cards
    // { id: 'rules', label: 'Configuration Rules', icon: Link2 },
    { id: 'customers', label: 'Customer Pricing', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pricing-page">
      <Header />
      {/* Page title + primary action — replaces the old back-button top-bar
          now that the global Admin Header handles cross-section navigation. */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Pricing System</h1>
              <p className="text-sm text-gray-500">Manage sales prices, supplier costs & product recipes</p>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'catalogs' && (
                <button
                  onClick={() => navigate('/admin/pricing/catalogs/new')}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Catalog
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  // BOM Management navigates directly to BOM page
                  if (tab.id === 'bom') {
                    navigate('/admin/bom');
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'catalogs' && (
          <CatalogsTab
            catalogs={filteredCatalogs}
            loading={loading}
            error={error}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onRefresh={fetchCatalogs}
          />
        )}
        {activeTab === 'sales' && <SalesRateCardTab />}
        {activeTab === 'freight' && <FreightRulesTab />}
        {activeTab === 'flows' && (
          // Redirecting to Flow Manager...
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}
        {activeTab === 'rules' && <ConfigurationRulesTab />}
        {activeTab === 'customers' && <CustomerPricingTab />}
        {activeTab === 'settings' && (
          <SettingsTab settings={settings} onUpdate={fetchSettings} />
        )}
      </div>
    </div>
  );
}

function CatalogsTab({ catalogs, loading, error, searchTerm, setSearchTerm, onRefresh }) {
  const navigate = useNavigate();
  const [editingCatalog, setEditingCatalog] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search catalogs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Catalogs Grid */}
      {catalogs.length === 0 ? (
        <div className="bg-white rounded-lg border border-border p-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No price catalogs yet</h3>
          <p className="text-gray-500 mb-4">Create your first catalog to start managing prices</p>
          <button
            onClick={() => navigate('/admin/pricing/catalogs/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Create Catalog
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {catalogs.map((catalog) => (
            <div
              key={catalog.id}
              className="bg-white rounded-lg border border-border p-5 hover:border-indigo-300 hover:shadow-premium-md transition-all group relative"
            >
              <div 
                className="cursor-pointer"
                onClick={() => navigate(`/admin/pricing/catalogs/${catalog.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <Package className="w-5 h-5 text-indigo-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{catalog.name}</h3>
                <p className="text-sm text-gray-500 mb-3">{catalog.partner_name || 'No supplier'}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{catalog.item_count} items</span>
                  <span className="font-medium text-gray-700">{catalog.currency}</span>
                </div>
              </div>
              {/* Edit button on hover */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCatalog(catalog);
                }}
                className="absolute top-3 right-10 p-1.5 bg-white border border-border rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50 hover:border-gray-300"
                title="Edit Catalog"
              >
                <Edit className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Edit Catalog Modal */}
      {editingCatalog && (
        <EditCatalogModalInline
          catalog={editingCatalog}
          onClose={() => setEditingCatalog(null)}
          onSave={() => {
            setEditingCatalog(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

function CustomerPricingTab() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data);
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  const filteredOrgs = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredOrgs.map((org) => (
              <tr key={org.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{org.name || 'Unnamed'}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 capitalize">{org.type || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{org.country || '-'}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => navigate(`/admin/pricing/customers/${org.id}`)}
                    className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                  >
                    Manage Pricing
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab({ settings, onUpdate }) {
  const [markup, setMarkup] = useState(settings?.default_markup_percent || 25);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setMarkup(settings.default_markup_percent);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/settings?default_markup_percent=${markup}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        onUpdate();
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Global Pricing Settings</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Markup Percentage
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={markup}
                onChange={(e) => setMarkup(parseFloat(e.target.value) || 0)}
                min="0"
                max="1000"
                step="0.1"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <span className="text-gray-500">%</span>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Applied to all items without a specific markup defined
            </p>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Available Units</h4>
            <div className="flex flex-wrap gap-2">
              {settings?.price_units?.map((unit) => (
                <span key={unit} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                  {unit}
                </span>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Supported Currencies</h4>
            <div className="flex flex-wrap gap-2">
              {settings?.currencies?.map((currency) => (
                <span key={currency} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                  {currency}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Pricing Logic</h4>
        <p className="text-sm text-blue-700">
          Price calculation follows this sequence:<br />
          <strong>Cost Price</strong> → <strong>+ Markup</strong> → <strong>- Customer Discount</strong> → <strong>Final Price</strong>
        </p>
      </div>
    </div>
  );
}

// Edit Catalog Modal Component (for PricingPage list view)
function EditCatalogModalInline({ catalog, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: catalog?.name || '',
    description: catalog?.description || '',
    partner_id: catalog?.partner_id || ''
  });
  const [partners, setPartners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch available partners
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/partners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPartners(data);
        }
      } catch (err) {
        console.error('Failed to fetch partners:', err);
      }
    };
    fetchPartners();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/pricing/catalogs/${catalog.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          partner_id: formData.partner_id || null
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update catalog');
      }
      
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-gray-900">Edit Catalog</h2>
          <p className="text-sm text-gray-500 mt-1">
            Update catalog name and supplier. Currency ({catalog?.currency}) cannot be changed.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catalog Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Vinyl Production 2025"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional description"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier / Partner</label>
            <select
              value={formData.partner_id}
              onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">No supplier assigned</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Changing supplier only affects this catalog&apos;s association. Existing purchases/jobs are unaffected.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-500">
              <strong>Currency:</strong> {catalog?.currency} <span className="text-gray-400">(immutable)</span>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
